import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { cx } from './cx'
import { Button, IconButton } from './Button'
import { daysInMonth, isoDate, monthName, monthShort, outOfRange, todayIso, WEEKDAYS_LONG, WEEKDAYS_NARROW } from './dateFormat'

export interface CalendarPickerProps {
  mode: 'date' | 'month'
  /** `yyyy-mm-dd` (mode date) o `yyyy-mm` (mode month), '' se nessuno. */
  value: string
  min?: string | undefined
  max?: string | undefined
  /** Mostra "Cancella". */
  clearable: boolean
  onPick: (iso: string) => void
  onClear: () => void
  onClose: () => void
}

function parts(iso: string): [number, number, number] | null {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(iso)
  return m ? [Number(m[1]), Number(m[2]), Number(m[3] ?? 1)] : null
}

/** Calendario M3: giorni (settimana dal lunedi'), selezione di mese e anno. Vive dentro un FloatingPanel. */
export function CalendarPicker({ mode, value, min, max, clearable, onPick, onClear, onClose }: CalendarPickerProps) {
  const today = todayIso()
  const start = parts(value) ?? parts(today) ?? [2000, 1, 1]
  const [y, setY] = useState(start[0])
  const [m, setM] = useState(start[1])
  const [focusDay, setFocusDay] = useState(start[2])
  const [view, setView] = useState<'days' | 'months' | 'years'>(mode === 'month' ? 'months' : 'days')
  const root = useRef<HTMLDivElement | null>(null)
  const wantFocus = useRef(true)

  const selected = parts(value)
  const dim = daysInMonth(y, m)
  const lead = (new Date(y, m - 1, 1).getDay() + 6) % 7
  const dayDisabled = (d: number) => outOfRange('date', isoDate(y, m, d), min, max)
  const monthDisabled = (mo: number) => outOfRange('month', `${String(y).padStart(4, '0')}-${String(mo).padStart(2, '0')}`, min, max)

  const goto = (d: Date) => {
    setY(d.getFullYear())
    setM(d.getMonth() + 1)
    setFocusDay(d.getDate())
    wantFocus.current = true
  }
  const addMonths = (delta: number) => {
    const t = new Date(y, m - 1 + delta, 1)
    t.setDate(Math.min(focusDay, daysInMonth(t.getFullYear(), t.getMonth() + 1)))
    return t
  }

  useEffect(() => {
    if (!wantFocus.current) return
    wantFocus.current = false
    const el = root.current?.querySelector<HTMLElement>('[data-roving="true"]')
    el?.focus()
  })

  const onGridKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const k = e.key
    const col = (lead + focusDay - 1) % 7
    const day = (delta: number) => new Date(y, m - 1, focusDay + delta)
    if (k === 'ArrowLeft') goto(day(-1))
    else if (k === 'ArrowRight') goto(day(1))
    else if (k === 'ArrowUp') goto(day(-7))
    else if (k === 'ArrowDown') goto(day(7))
    else if (k === 'Home') goto(day(-col))
    else if (k === 'End') goto(day(6 - col))
    else if (k === 'PageUp') goto(addMonths(e.shiftKey ? -12 : -1))
    else if (k === 'PageDown') goto(addMonths(e.shiftKey ? 12 : 1))
    else return
    e.preventDefault()
  }

  const shiftMonth = (delta: number) => {
    const d = new Date(y, m - 1 + delta, 1)
    setY(d.getFullYear())
    setM(d.getMonth() + 1)
    setFocusDay((f) => Math.min(f, daysInMonth(d.getFullYear(), d.getMonth() + 1)))
  }

  const header = (
    <div className="md-cal__header">
      <button
        type="button"
        className="md-cal__title md-state"
        aria-live="polite"
        aria-label={view === 'years' ? 'Scegli l\'anno' : 'Cambia anno o mese'}
        onClick={() => setView((v) => (v === 'days' ? 'months' : v === 'months' ? 'years' : mode === 'month' ? 'months' : 'days'))}
      >
        {view === 'days' ? `${monthName(m)} ${y}` : view === 'months' ? String(y) : 'Anno'}
        <span className="material-symbols-outlined md-icon md-icon--20" aria-hidden="true">{view === 'days' ? 'arrow_drop_down' : 'arrow_drop_up'}</span>
      </button>
      <span className="md-cal__nav">
        {view === 'days' ? (
          <>
            <IconButton icon="chevron_left" size="sm" aria-label="Mese precedente" onClick={() => shiftMonth(-1)} />
            <IconButton icon="chevron_right" size="sm" aria-label="Mese successivo" onClick={() => shiftMonth(1)} />
          </>
        ) : view === 'months' ? (
          <>
            <IconButton icon="chevron_left" size="sm" aria-label="Anno precedente" onClick={() => setY(y - 1)} />
            <IconButton icon="chevron_right" size="sm" aria-label="Anno successivo" onClick={() => setY(y + 1)} />
          </>
        ) : null}
      </span>
    </div>
  )

  let body
  if (view === 'years') {
    const years = Array.from({ length: 201 }, (_, i) => y - 100 + i)
    body = (
      <div className="md-cal__years" role="listbox" aria-label="Anno">
        {years.map((yy) => (
          <button
            key={yy}
            type="button"
            role="option"
            aria-selected={yy === y}
            ref={(el) => { if (el && yy === y) el.scrollIntoView({ block: 'center' }) }}
            className={cx('md-cal__cell', 'md-cal__cell--wide', 'md-state', yy === y && 'md-cal__cell--selected')}
            onClick={() => { setY(yy); setView(mode === 'month' ? 'months' : 'days') }}
          >
            {yy}
          </button>
        ))}
      </div>
    )
  } else if (view === 'months') {
    body = (
      <div className="md-cal__months">
        {Array.from({ length: 12 }, (_, i) => i + 1).map((mo) => {
          const isSel = selected ? selected[0] === y && selected[1] === mo : false
          return (
            <button
              key={mo}
              type="button"
              disabled={monthDisabled(mo)}
              aria-pressed={isSel}
              aria-label={`${monthName(mo)} ${y}`}
              className={cx('md-cal__cell', 'md-cal__cell--wide', 'md-state', isSel && 'md-cal__cell--selected')}
              onClick={() => {
                if (mode === 'month') onPick(`${String(y).padStart(4, '0')}-${String(mo).padStart(2, '0')}`)
                else { setM(mo); setView('days') }
              }}
            >
              {monthShort(mo)}
            </button>
          )
        })}
      </div>
    )
  } else {
    const roving = Math.min(focusDay, dim)
    body = (
      <div role="grid" aria-label={`${monthName(m)} ${y}`} className="md-cal__grid" onKeyDown={onGridKey}>
        <div role="row" className="md-cal__week">
          {WEEKDAYS_NARROW.map((w, i) => (
            <span key={i} role="columnheader" aria-label={WEEKDAYS_LONG[i]} className="md-cal__dow">{w}</span>
          ))}
        </div>
        <div className="md-cal__days">
          {Array.from({ length: lead }, (_, i) => <span key={`b${i}`} className="md-cal__blank" />)}
          {Array.from({ length: dim }, (_, i) => i + 1).map((d) => {
            const iso = isoDate(y, m, d)
            const isSel = selected ? selected[0] === y && selected[1] === m && selected[2] === d : false
            return (
              <button
                key={d}
                type="button"
                role="gridcell"
                data-roving={d === roving ? 'true' : undefined}
                tabIndex={d === roving ? 0 : -1}
                disabled={dayDisabled(d)}
                aria-selected={isSel}
                aria-current={iso === today ? 'date' : undefined}
                aria-label={`${d} ${monthName(m).toLowerCase()} ${y}`}
                className={cx('md-cal__cell', 'md-state', isSel && 'md-cal__cell--selected', iso === today && 'md-cal__cell--today')}
                onClick={() => onPick(iso)}
              >
                {d}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  const todayOk = mode === 'date' ? !outOfRange('date', today, min, max) : !outOfRange('month', today.slice(0, 7), min, max)
  return (
    <div
      ref={root}
      className="md-cal"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          onClose()
        }
      }}
    >
      <p className="md-cal__headline">{mode === 'month' ? 'Seleziona mese' : 'Seleziona data'}</p>
      {header}
      {body}
      <div className="md-cal__actions">
        {clearable ? <Button variant="text" size="sm" onClick={onClear}>Cancella</Button> : <span />}
        <span className="md-cal__actions-end">
          {todayOk ? <Button variant="text" size="sm" onClick={() => onPick(mode === 'date' ? today : today.slice(0, 7))}>{mode === 'date' ? 'Oggi' : 'Questo mese'}</Button> : null}
          <Button variant="text" size="sm" onClick={onClose}>Annulla</Button>
        </span>
      </div>
    </div>
  )
}
